import { Loader2, Send, Share2 } from "lucide-react";
import React from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Select from "../ui/Select";

interface PublishPostModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (publishType?: string) => void;
  loading: boolean;
  postId: number | null;
  publishOptions?: { value: string; label: string }[];
  selectedOption?: string;
  setSelectedOption?: (value: string) => void;
}

const PublishPostModal: React.FC<PublishPostModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  postId,
  publishOptions,
  selectedOption,
  setSelectedOption,
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Publicar Post")}
      maxWidth="max-w-lg"
      footer={
        <div className="flex flex-row justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            icon={
              loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )
            }
            onClick={() => onConfirm(selectedOption)}
            disabled={loading}
          >
            {loading ? t("Publicando...") : t("Confirmar Publicação")}
          </Button>
        </div>
      }
    >
      <div className="py-2">
        <div className="flex items-start gap-4 mb-6">
          <div
            className={cn(
              "shrink-0 w-12 h-12 rounded-full flex items-center justify-center",
              "bg-green-100 text-green-600",
            )}
          >
            <Share2 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {t("Confirmar Publicação")}
            </h3>
            <p className="text-sm text-gray-600">
              {t("O post")}{" "}
              <span className="font-semibold text-gray-900">#{postId}</span>{" "}
              {t("será publicado agora na plataforma selecionada.")}
            </p>
          </div>
        </div>

        {publishOptions && publishOptions.length > 0 && setSelectedOption && (
          <div className="mb-4 space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t("Tipo de Publicação")}
            </label>
            <Select
              value={selectedOption || publishOptions[0]?.value || ""}
              onChange={(v) => setSelectedOption(v)}
              options={publishOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t("Escolha como o post será publicado na plataforma")}
            </p>
          </div>
        )}

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-xs font-medium text-green-800">
            ✓ {t("O post será publicado imediatamente após a confirmação")}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default PublishPostModal;
